import { useRef, useState, useCallback } from 'react';

/**
 * Hook para modais redimensionáveis estilo Windows.
 *
 * Uso:
 *   const { modalRef, modalStyle, modalClassName, resizing, iniciarResize } =
 *     useResizableModal({ storageKey: 'meuModal', minWidth: 560, minHeight: 360 });
 *
 *   <div ref={modalRef} className={`${styles.modal} ${modalClassName}`} style={modalStyle}>
 *     <ResizableHandles onResize={iniciarResize} />
 *     ...
 *   </div>
 *
 * Cada modal pode ter sua própria storageKey — o tamanho é persistido em
 * localStorage individualmente.
 */
export function useResizableModal({
    storageKey,
    minWidth  = 560,
    minHeight = 360,
    maxWidthRatio  = 0.98,  // 98% da viewport
    maxHeightRatio = 0.98,
} = {}) {
    const modalRef = useRef(null);

    const [dimensoes, setDimensoes] = useState(() => {
        if (!storageKey) return null;
        try {
            const raw = localStorage.getItem(`modalSize:${storageKey}`);
            if (raw) {
                const d = JSON.parse(raw);
                if (d && typeof d.width === 'number' && typeof d.height === 'number') {
                    return d;
                }
            }
        } catch { /* localStorage indisponível */ }
        return null;
    });

    const [resizing, setResizing] = useState(false);

    const iniciarResize = useCallback((direcao, ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        const el = modalRef.current;
        if (!el) return;

        const rect   = el.getBoundingClientRect();
        const startX = ev.clientX;
        const startY = ev.clientY;
        const startW = rect.width;
        const startH = rect.height;

        setResizing(true);
        document.body.style.cursor =
            direcao === 'br' ? 'nwse-resize' :
            direcao === 'bl' ? 'nesw-resize' :
            direcao === 'r' || direcao === 'l' ? 'ew-resize' :
            'ns-resize';

        const onMove = (e) => {
            const dx = e.clientX - startX;
            const dy = e.clientY - startY;
            const maxW = window.innerWidth  * maxWidthRatio;
            const maxH = window.innerHeight * maxHeightRatio;
            let w = startW;
            let h = startH;

            if (direcao === 'r'  || direcao === 'br')                       w = startW + dx;
            if (direcao === 'l'  || direcao === 'bl')                       w = startW - dx;
            if (direcao === 'b'  || direcao === 'br' || direcao === 'bl')   h = startH + dy;

            w = Math.max(minWidth,  Math.min(maxW, w));
            h = Math.max(minHeight, Math.min(maxH, h));
            setDimensoes({ width: w, height: h });
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup',   onUp);
            document.body.style.cursor = '';
            setResizing(false);
            // Persistência por storageKey
            if (storageKey && modalRef.current) {
                const r = modalRef.current.getBoundingClientRect();
                try {
                    localStorage.setItem(
                        `modalSize:${storageKey}`,
                        JSON.stringify({ width: r.width, height: r.height })
                    );
                } catch { /* ignore */ }
            }
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup',   onUp);
    }, [storageKey, minWidth, minHeight, maxWidthRatio, maxHeightRatio]);

    const modalStyle = dimensoes
        ? { width: dimensoes.width, height: dimensoes.height, maxWidth: 'none', maxHeight: 'none' }
        : undefined;

    return {
        modalRef,
        modalStyle,
        modalClassName: resizing ? 'modal-resizing' : '',
        dimensoes,
        resizing,
        iniciarResize,
    };
}
